import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, User, Mail, Calendar, Clock, AlertCircle, IndianRupee, CreditCard, ChevronLeft, ChevronRight, Phone, PhoneCall } from "lucide-react";
import { bookClass, verifyPaymentAPI } from "./fetchClasses";
import { sendOtp, verifyOtp } from "../instrutorProfile/profileAPIs";
import BookingConfirmationModal from "../BookingConfirmationModal";
import toast from "react-hot-toast";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay } from "date-fns";

// Declare Razorpay for TypeScript
declare global {
    interface Window {
        Razorpay: any;
    }
}

interface UserDetails {
    name: string;
    email: string;
    age: string;
    phone: string;
}

interface ClassBookingProps {
    classData: any;
    onClose: () => void;
    onBookingSuccess: (bookingId: string) => void;
    ClassId: string;
    userDetails: UserDetails | null;
    demoCompleted: boolean;
    vendorId?: string | null;
}

interface BookingFormData {
    name: string;
    email: string;
    age: string;
    preferredTimeSlot: string;
    pricingOption: string;
    phone?: string;
    selectedDates: string[]; // dates selected by user
}

interface FormErrors {
    name?: string;
    email?: string;
    age?: string;
    preferredTimeSlot?: string;
    pricingOption?: string;
    phone?: string;
    selectedDates?: string;
}

interface PricingOption {
    type: 'demo' | 'free' | 'session' | 'monthly' | 'yearly';
    label: string;
    price: number;
    description: string;
}

interface bookingRes {
    data: {
        bookingId: string;
        razorpayOrderId?: string;
        amount?: number;
        currency?: string;
        pendingBookingData?: any; // Add this for paid bookings
    };
    message: string;
}

interface RazorpayResponse {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
}

interface VerificationResponse {
    success: boolean;
    message: string;
    bookingId?: string; // Add this to get the actual booking ID after creation
}

export default function ClassBooking({ classData, onClose, onBookingSuccess, ClassId, userDetails, demoCompleted, vendorId }: ClassBookingProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<BookingFormData>({
        name: '',
        email: '',
        age: '',
        preferredTimeSlot: '',
        pricingOption: '',
        phone: '',
        selectedDates: [],
    });

    // OTP state
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [isSendingOTP, setIsSendingOTP] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const [phoneForOtp, setPhoneForOtp] = useState('');

    useEffect(() => {
        if (userDetails) {
            setFormData(prev => ({
                ...prev,
                name: userDetails.name || '',
                email: userDetails.email || '',
                age: userDetails.age || '',
                phone: userDetails.phone || '',
            }));
            // Auto-verify if phone is already provided
            if (userDetails.phone) {
                setOtpVerified(true);
            }
        }
    }, [userDetails]);

    const formatMeetTime = (value?: string) => {
        if (!value) return 'TBD';
        return value
            .replace(/undefined/gi, '')
            .replace(/\s+/g, ' ')
            .replace(/\s+,/g, ',')
            .trim();
    };

    // Resend timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

    // Confirmation modal state
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingBookingData, setPendingBookingData] = useState<any>(null);

    // Load Razorpay script
    useEffect(() => {
        const loadRazorpay = () => {
            return new Promise((resolve) => {
                if (window.Razorpay) {
                    setIsRazorpayLoaded(true);
                    resolve(true);
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.onload = () => {
                    setIsRazorpayLoaded(true);
                    resolve(true);
                };
                script.onerror = () => {
                    resolve(false);
                };
                document.body.appendChild(script);
            });
        };

        loadRazorpay();
    }, []);

    // Get available pricing options from classData
    const getAvailablePricingOptions = (): PricingOption[] => {
        const options: PricingOption[] = [];

        // Check if it's a demo class and if demo is not completed
        if (classData?.pricingType === 'demo' && !demoCompleted) {
            options.push({
                type: 'demo',
                label: `Free Demo (${classData.demoDuration} day)`,
                price: 0,
                description: 'Try this class for free for a limited time.'
            });
        }

        // Check if it's a free class
        if (classData?.purchaseStatus === 'Free Tier') {
            options.push({
                type: 'free',
                label: 'Free Access',
                price: 0,
                description: 'Complete access at no cost'
            });
        }

        // Add session-wise pricing if available
        if (classData?.classPrice?.session && classData.classPrice.session > 0) {
            options.push({
                type: 'session',
                label: 'Per Session',
                price: classData.classPrice.session,
                description: 'Pay for individual sessions'
            });
        }

        // Add monthly pricing if available
        if (classData?.classPrice?.monthly && classData.classPrice.monthly > 0) {
            options.push({
                type: 'monthly',
                label: 'Monthly Subscription',
                price: classData.classPrice.monthly,
                description: 'Unlimited access for one month'
            });
        }

        // Add yearly pricing if available
        if (classData?.classPrice?.yearly && classData.classPrice.yearly > 0) {
            options.push({
                type: 'yearly',
                label: 'Yearly Subscription',
                price: classData.classPrice.yearly,
                description: 'Best value - Full year access'
            });
        }

        // If no pricing options are available, default to free
        if (options.length === 0) {
            options.push({
                type: 'free',
                label: 'Free Access',
                price: 0,
                description: 'No payment required'
            });
        }

        return options;
    };

    const pricingOptions = getAvailablePricingOptions();
    const selectedPricing = pricingOptions.find(option => option.type === formData.pricingOption);

    // Get available time slots from classData
    const getAvailableTimeSlots = () => {
        if (classData?.googleMeetLinks && Array.isArray(classData.googleMeetLinks)) {
            return classData.googleMeetLinks.map((link: any, index: number) => ({
                value: formatMeetTime(link.time) || `slot-${index + 1}`,
                label: `Slot ${index + 1}: ${formatMeetTime(link.time)}`
            }));
        }
        return [];
    };

    const timeSlots = getAvailableTimeSlots();

    // Generate available dates based on pricing type and class schedule
    const getAvailableDates = () => {
        const dates: Array<{ date: Date; dayName: string; dateStr: string }> = [];

        if (!classData?.schedule || !classData?.classStartDate) {
            return dates;
        }

        const startDate = new Date(classData.classStartDate);
        
        let endDate;
        if (classData.classEndDate) {
            endDate = new Date(classData.classEndDate);
        } else {
            // Default to 12 months ahead for recurring classes without an end date
            endDate = new Date(Math.max(Date.now(), startDate.getTime()));
            endDate.setMonth(endDate.getMonth() + 12);
        }

        // For demo, limit to demo duration
        let dateLimit = endDate;
        if (formData.pricingOption === 'demo' && classData?.demoDuration) {
            const demoDays = parseInt(classData.demoDuration) || 7;
            dateLimit = new Date(startDate);
            dateLimit.setDate(dateLimit.getDate() + demoDays);
        }

        // Get active days from schedule (0 = Sunday, 1 = Monday, etc.)
        const activeDays = new Set<number>();
        const dayMapping: { [key: string]: number } = {
            'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4, 'friday': 5, 'saturday': 6, 'sunday': 0
        };

        Object.entries(classData.schedule).forEach(([day, config]: [string, any]) => {
            if (config.isActive) {
                const dayNum = dayMapping[day.toLowerCase()];
                if (dayNum !== undefined) activeDays.add(dayNum);
            }
        });

        // Generate dates
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const currentDate = new Date(Math.max(startDate.getTime(), now.getTime()));
        while (currentDate <= dateLimit) {
            if (activeDays.has(currentDate.getDay())) {
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                dates.push({
                    date: new Date(currentDate),
                    dayName: dayNames[currentDate.getDay()],
                    dateStr: format(currentDate, 'yyyy-MM-dd')
                });
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    };

    const availableDates = getAvailableDates();

    // State for calendar view
    const [currentMonth, setCurrentMonth] = useState(() => {
        const startDate = new Date(classData?.classStartDate || new Date());
        return new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    });

    // Get available date strings for quick lookup
    const availableDateSet = new Set(availableDates.map(d => d.dateStr));

    // Get calendar days for current month
    const getCalendarDays = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

        // Pad with previous month's days
        const startDay = getDay(monthStart);
        const previousDays = [];
        for (let i = 0; i < startDay; i++) {
            const date = new Date(monthStart);
            date.setDate(date.getDate() - (startDay - i));
            previousDays.push(date);
        }

        return [...previousDays, ...daysInMonth];
    };

    const calendarDays = getCalendarDays();

    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const isDateAvailable = (date: Date): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const day = new Date(date);
        day.setHours(0, 0, 0, 0);
        if (day < today) return false;

        const dateStr = format(date, 'yyyy-MM-dd');
        return availableDateSet.has(dateStr);
    };

    const isDateSelected = (date: Date): boolean => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return formData.selectedDates.includes(dateStr);
    };

    const handleDateClick = (date: Date) => {
        if (!isDateAvailable(date)) return;
        const dateStr = format(date, 'yyyy-MM-dd');
        toggleDateSelection(dateStr);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Special handling for phone field - only allow numbers
        if (name === 'phone') {
            // Remove any non-numeric characters
            const numericValue = value.replace(/\D/g, '');
            // Limit to 10 digits
            const limitedValue = numericValue.slice(0, 10);
            
            setFormData(prev => ({
                ...prev,
                [name]: limitedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                // Only reset selected dates when pricing option changes
                ...(name === 'pricingOption' && { selectedDates: [] })
            }));
        }

        // Clear error when user starts typing
        if (errors[name as keyof BookingFormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const toggleDateSelection = (dateStr: string) => {
        setFormData(prev => ({
            ...prev,
            selectedDates: prev.selectedDates.includes(dateStr)
                ? prev.selectedDates.filter(d => d !== dateStr)
                : [...prev.selectedDates, dateStr]
        }));
    };

    // Send OTP
    const handleSendOtp = async () => {
        const phone = (formData.phone || '').replace(/\D/g, '');

        if (!/^\d{10}$/.test(phone)) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }

        setIsSendingOTP(true);
        setOtpError('');

        try {
            const res = await sendOtp(phone);

            if (res.success) {
                toast.success('OTP sent successfully!');
                setOtpSent(true);
                setPhoneForOtp(phone);
                setResendTimer(60);
                setOtp(['', '', '', '', '', '']);
                setTimeout(() => {
                    const firstInput = document.getElementById('otp-input-0') as HTMLInputElement;
                    firstInput?.focus();
                }, 100);
            } else {
                toast.error(res.message || 'Failed to send OTP');
            }
        } catch {
            toast.error('Failed to send OTP. Please try again.');
        } finally {
            setIsSendingOTP(false);
        }
    };

    // Verify OTP
    const handleVerifyOtp = async () => {
        const otpValue = otp.join('');

        if (otpValue.length !== 6) {
            toast.error('Please enter complete 6-digit OTP');
            return;
        }

        setIsVerifyingOTP(true);

        try {
            const res = await verifyOtp(phoneForOtp, otpValue);

            if (res.success && res.verified) {
                toast.success('Phone number verified successfully!');
                setOtpVerified(true);
                setOtpError('');
            } else {
                setOtpError(res.message || 'Invalid OTP. Please try again.');
                setOtp(['', '', '', '', '', '']);
                const firstInput = document.getElementById('otp-input-0') as HTMLInputElement;
                firstInput?.focus();
            }
        } catch {
            setOtpError('Verification failed. Please try again.');
        } finally {
            setIsVerifyingOTP(false);
        }
    };

    // Handle OTP input changes
    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`) as HTMLInputElement;
            nextInput?.focus();
        }
    };

    // Handle OTP input keydown events
    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-input-${index - 1}`) as HTMLInputElement;
            prevInput?.focus();
        }
    };

    // Reset phone verification to allow entering a new number
    const resetPhoneVerification = () => {
        setFormData(prev => ({ ...prev, phone: '' }));
        setOtp(['', '', '', '', '', '']);
        setOtpSent(false);
        setOtpVerified(false);
        setOtpError('');
        setPhoneForOtp('');
        setResendTimer(0);
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Age validation
         if (!formData.age.trim()) {
             newErrors.age = 'Age is required';
         } else {
             const ageNum = parseInt(formData.age);
             if (isNaN(ageNum) || ageNum < 5 || ageNum > 100) {
                 newErrors.age = 'Please enter a valid age between 5 and 100';
             }
         }

         // Phone validation
         const phone = (formData.phone || '').trim();
         if (!phone) {
             newErrors.phone = 'Phone number is required';
         } else if (!/^\d{10}$/.test(phone)) {
             newErrors.phone = 'Phone number must be exactly 10 digits (numbers only)';
         }

         // Preferred time slot validation
        if (!formData.preferredTimeSlot) {
            newErrors.preferredTimeSlot = 'Please select a preferred time slot';
        }

        // Pricing option validation
        if (!formData.pricingOption) {
            newErrors.pricingOption = 'Please select a pricing option';
        }

        // Selected dates validation - only for session/demo
        if ((formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length === 0) {
            newErrors.selectedDates = 'Please select at least one date';
        }

        // Phone verification validation
        if (!otpVerified) {
            toast.error('Please verify your phone number');
        }

        setErrors(newErrors);
        
        // Scroll to first error field
        if (Object.keys(newErrors).length > 0) {
            const firstErrorField = Object.keys(newErrors)[0];
            const element = document.getElementById(firstErrorField);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
            toast.error('Please fill all required fields');
        }
        
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Calculate total amount based on pricing and selected dates
        let totalAmount = selectedPricing?.price || 0;
        if ((formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length > 0) {
            totalAmount = totalAmount * formData.selectedDates.length;
        }

        // Prepare booking details for confirmation modal
        const bookingDetails = {
            serviceName: classData.classTitle,
            serviceType: 'class' as const,
            dates: formData.selectedDates.length > 0 ? formData.selectedDates : undefined,
            timeSlot: formData.preferredTimeSlot,
            instructorName: classData.mentorName || 'Your Instructor',
            amount: totalAmount,
            pricingType: selectedPricing?.label,
            userDetails: {
                name: formData.name,
                email: formData.email,
                phone: formData.phone || '',
                age: formData.age,
            },
            additionalInfo: {
                classCategory: classData.classCategory,
                classDuration: classData.classDuration,
            }
        };

        // Prepare booking data for API call
        const bookingData = {
            classId: classData._id,
            className: classData.classTitle,
            classDays: classData.classDays,
            ...formData,
            selectedPricing: selectedPricing,
            totalAmount: totalAmount,
            vendorId: vendorId,
            bookingDate: new Date().toISOString(),
            status: totalAmount === 0 ? 'confirmed' : 'pending_payment',
            isDemo: selectedPricing?.type === 'demo'
        };

        setPendingBookingData({ bookingData, bookingDetails });
        setShowConfirmationModal(true);
    };

    const handleConfirmBooking = async () => {
        if (!pendingBookingData) return;

        setShowConfirmationModal(false);
        setIsSubmitting(true);
        setSubmitStatus('idle');

        // Add a timeout for the booking process
        const bookingTimeout = setTimeout(() => {
            setIsSubmitting(false);
            setSubmitStatus('error');
            toast.error("Booking is taking too long. Please try again.");
        }, 30000); // 30 seconds timeout

        try {
            const { bookingData } = pendingBookingData;

            // Call the API to create booking
            const response = await bookClass(bookingData) as bookingRes;
            clearTimeout(bookingTimeout); // Clear the timeout if the API responds in time

            if (response && response.data) {
                // For free bookings (including demos), booking is complete
                if (bookingData.totalAmount === 0) {
                    const bookingId = response.data.bookingId;
                    if (bookingId) {
                        toast.success('Free booking confirmed!');
                        setSubmitStatus('success');
                        setIsSubmitting(false);
                        onBookingSuccess(bookingId);
                        navigate('/profile');
                        onClose();
                    } else {
                        // Booking succeeded even without explicit bookingId
                        toast.success('Booking confirmed!');
                        setSubmitStatus('success');
                        setIsSubmitting(false);
                        navigate('/profile');
                        onClose();
                    }
                } else {
                    // For paid bookings, initiate Razorpay payment
                    if (!isRazorpayLoaded || !response.data.razorpayOrderId) {
                        toast.error('Payment system not ready. Please try again.');
                        setIsSubmitting(false);
                        return;
                    }

                    if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
                        toast.error('Razorpay is not configured.');
                        setIsSubmitting(false);
                        return;
                    }

                    const options = {
                        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                        amount: response.data.amount,
                        currency: response.data.currency,
                        name: 'Metafit Wellness',
                        description: `Booking for ${classData.classTitle}`,
                        image: '/metalogo.webp', // Optional: Add your logo
                        order_id: response.data.razorpayOrderId,
                        handler: async (razorpayResponse: RazorpayResponse) => {
                            // IMPORTANT: Now include pendingBookingData in verification
                            const paymentData = {
                                ...razorpayResponse,
                                bookingId: response.data.bookingId,
                                pendingBookingData: response.data.pendingBookingData // This contains all booking details
                            };

                            try {
                                const verificationRes = await verifyPaymentAPI(paymentData) as VerificationResponse;

                                if (verificationRes.success) {
                                    toast.success(verificationRes.message || 'Payment successful! Your booking is confirmed.');
                                    setSubmitStatus('success');
                                    setIsSubmitting(false);
                                    onBookingSuccess(verificationRes.bookingId || response.data.bookingId);
                                    navigate('/profile');
                                    onClose();
                                } else {
                                    toast.error(verificationRes.message || 'Payment verification failed. Please contact support.');
                                    setSubmitStatus('error');
                                    setIsSubmitting(false);
                                }
                            } catch (error: any) {
                                const errorMessage = error.response?.data?.message || 'An error occurred during payment verification.';
                                toast.error(errorMessage);
                                setSubmitStatus('error');
                                setIsSubmitting(false);
                            }
                        },
                        prefill: {
                            name: formData.name,
                            email: formData.email,
                            contact: formData.phone,
                        },
                        notes: {
                            bookingId: response.data.bookingId,
                            classId: classData._id,
                        },
                        theme: {
                            color: '#0d9488', // Teal color from your design
                        },
                        modal: {
                            ondismiss: async () => {
                                toast.error('Payment was cancelled.');
                                setIsSubmitting(false);
                                // No need to call cancelBookingAPI since no booking was created yet
                                // The booking will only be created after successful payment
                                console.log('Payment modal dismissed - no booking was created');
                            }
                        }
                    };

                    const rzp = new window.Razorpay(options);
                    rzp.open();

                    rzp.on('payment.failed', function (response: any) {
                        toast.error(`Payment failed: ${response.error.description}`);
                        setIsSubmitting(false);
                        // No booking was created, so no need to cancel anything
                    });
                }
            } else if (response.message && response.message !== 'Booking successful') {
                toast.error(response.message);
                setSubmitStatus('error');
                setIsSubmitting(false);
            } else {
                // Unknown response format but still handle gracefully
                toast.success('Booking confirmed!');
                setSubmitStatus('success');
                setIsSubmitting(false);
                navigate('/profile');
                onClose();
            }
        } catch (error: any) {
            clearTimeout(bookingTimeout); // Clear the timeout on error as well
            console.error('Booking error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
            setSubmitStatus('error');
            setIsSubmitting(false);
        }
    };
    return (
        <React.Fragment>
            <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Book Your Class</h2>
                    <p className="text-gray-600 text-sm mt-1">
                        {classData?.classTitle || 'Class Booking'}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <X className="h-5 w-5 text-gray-500" />
                </button>
            </div>

            {/* Class Info Summary */}
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between text-sm">
                    <div>
                        <div className="font-medium text-gray-900">
                            {classData?.classCategory || 'Category'}
                        </div>
                        <div className="text-gray-600">
                            Duration: {classData?.classDuration || 'N/A'}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-bold text-lg text-teal-600">
                            {selectedPricing ? (
                                selectedPricing.price === 0 ? 'Free' :
                                    (formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length > 0
                                        ? `₹${selectedPricing.price * formData.selectedDates.length}`
                                        : `₹${selectedPricing.price}`
                            ) : 'Select pricing'}
                        </div>
                        <div className="text-xs text-gray-500">
                            {(formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length > 0
                                ? `${formData.selectedDates.length} session(s) × ₹${selectedPricing?.price}`
                                : `${timeSlots.length} time slots available`
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div id="name">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="h-4 w-4 inline mr-1" />
                        Full Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Your full name"
                    />
                    {errors.name && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Email Field */}
                <div id="email">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="h-4 w-4 inline mr-1" />
                        Email Address *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Your email address"
                    />
                    {errors.email && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.email}
                        </p>
                    )}
                </div>

                {/* Age and Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div id="age">
                        <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            Age *
                        </label>
                        <input
                            type="number"
                            id="age"
                            name="age"
                            value={formData.age}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.age ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                            placeholder="Your age"
                        />
                        {errors.age && (
                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {errors.age}
                            </p>
                        )}
                    </div>

                    <div id="phone">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            <PhoneCall className="h-4 w-4 inline mr-1" />
                            Phone Number (10 digits) *
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            maxLength={10}
                            inputMode="numeric"
                            pattern="[0-9]{10}"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                            placeholder="Enter 10-digit phone number"
                        />
                        <div className="flex items-center gap-3 mt-1">
                            {formData.phone && formData.phone.length === 10 ? (
                                <p className="text-green-600 text-xs flex items-center">
                                    <span className="h-4 w-4 rounded-full bg-green-500 text-white flex items-center justify-center mr-1 text-[10px]">✓</span>
                                    Valid phone number
                                </p>
                            ) : formData.phone && formData.phone.length < 10 ? (
                                <p className="text-gray-500 text-xs flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {10 - formData.phone.length} digit(s) remaining
                                </p>
                            ) : null}
                        </div>
                        {errors.phone && (
                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {errors.phone}
                            </p>
                        )}
                    </div>
                </div>

                {/* Pricing Options */}
                <div id="pricingOption">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        <IndianRupee className="h-4 w-4 inline mr-1" />
                        Select Pricing Option *
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                        {pricingOptions.map((option) => (
                            <div
                                key={option.type}
                                className={`relative border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${formData.pricingOption === option.type ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-gray-300 hover:border-gray-400'}`}
                                onClick={() => handleInputChange({ target: { name: 'pricingOption', value: option.type } } as any)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            name="pricingOption"
                                            value={option.type}
                                            checked={formData.pricingOption === option.type}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-teal-600 focus:ring-teal-500"
                                        />
                                        <div className="ml-3">
                                            <div className="font-medium text-gray-900">
                                                {option.label}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {option.description}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`font-bold text-lg ${option.price === 0 ? 'text-green-600' : 'text-teal-600'}`}>
                                            {option.price === 0 ? 'Free' : `₹${option.price}`}
                                        </div>
                                        {option.type === 'yearly' && option.price > 0 && (
                                            <div className="text-xs text-green-600 font-medium">
                                                Best Value!
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {option.price > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="flex items-center text-xs text-gray-500">
                                            <CreditCard className="h-3 w-3 mr-1" />
                                            Secure payment via Razorpay
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {errors.pricingOption && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.pricingOption}
                        </p>
                    )}
                </div>

                {/* Preferred Time Slot */}
                <div id="preferredTimeSlot">
                    <label htmlFor="preferredTimeSlot" className={`block text-sm font-medium mb-1 ${errors.preferredTimeSlot ? 'text-red-700' : 'text-gray-700'}`}>
                        <Clock className="h-4 w-4 inline mr-1" />
                        Preferred Time Slot *
                    </label>
                    <select
                        id="preferredTimeSlot"
                        name="preferredTimeSlot"
                        value={formData.preferredTimeSlot}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.preferredTimeSlot ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}>
                        <option value="">Select your preferred time slot</option>
                        {timeSlots.map((slot: any, index: any) => (
                            <option key={index} value={slot.value}>
                                {slot.label}
                            </option>
                        ))}
                        {timeSlots.length === 0 && (
                            <option value="flexible">Flexible with timing</option>
                        )}
                    </select>
                    {errors.preferredTimeSlot && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.preferredTimeSlot}
                        </p>
                    )}
                </div>

                {/* Date Selection Calendar for Session/Demo */}
                {(formData.pricingOption === 'session' || formData.pricingOption === 'demo') && availableDates.length > 0 && (
                    <div id="selectedDates">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            {formData.pricingOption === 'demo' ? 'Select Demo Date(s)' : 'Select Session Date(s)'} *
                        </label>
                        <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            {/* Calendar Header */}
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    type="button"
                                    onClick={handlePrevMonth}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                                </button>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {format(currentMonth, 'MMMM yyyy')}
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleNextMonth}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <ChevronRight className="h-5 w-5 text-gray-600" />
                                </button>
                            </div>

                            {/* Day Headers */}
                            <div className="grid grid-cols-7 gap-2 mb-3">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar Days */}
                            <div className="grid grid-cols-7 gap-2">
                                {calendarDays.map((date, index) => {
                                    const isAvailable = isDateAvailable(date);
                                    const isSelected = isDateSelected(date);
                                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();

                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => handleDateClick(date)}
                                            disabled={!isAvailable}
                                            className={`
                                                aspect-square rounded-lg flex items-center justify-center text-sm font-medium
                                                transition-all
                                                ${!isCurrentMonth ? 'text-gray-300 bg-gray-50 cursor-default' : ''}
                                                ${isCurrentMonth && !isAvailable ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : ''}
                                                ${isCurrentMonth && isAvailable && !isSelected ? 'text-gray-700 bg-white border border-gray-300 hover:border-teal-400 hover:bg-teal-50 cursor-pointer' : ''}
                                                ${isSelected ? 'bg-teal-500 text-white border border-teal-600 shadow-md' : ''}
                                            `}
                                        >
                                            {format(date, 'd')}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Legend */}
                            <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-600 space-y-1">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-teal-500 rounded border border-teal-600"></div>
                                    <span>Selected</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
                                    <span>Available</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-gray-100 rounded"></div>
                                    <span>Not available</span>
                                </div>
                            </div>
                        </div>

                        {errors.selectedDates && (
                            <p className="text-red-500 text-xs mt-2 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {errors.selectedDates}
                            </p>
                        )}

                        {formData.selectedDates.length > 0 && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800 font-medium mb-2">
                                    <strong>{formData.selectedDates.length}</strong> date(s) selected:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {formData.selectedDates.sort().map(dateStr => (
                                        <span key={dateStr} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                            {format(new Date(dateStr + 'T00:00:00'), 'd MMM')}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Pricing Summary */}
                {selectedPricing && (
                    <div className="bg-gradient-to-r from-teal-50 to-green-50 border border-teal-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-sm text-gray-600 font-medium">Price per {formData.pricingOption === 'demo' ? 'day' : formData.pricingOption === 'session' ? 'session' : 'month'}</p>
                                <p className="text-2xl font-bold text-teal-600">₹{selectedPricing.price === 0 ? 'Free' : selectedPricing.price}</p>
                            </div>
                            {(formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length > 0 && (
                                <div className="text-right">
                                    <p className="text-sm text-gray-600 font-medium">{formData.selectedDates.length} × ₹{selectedPricing.price}</p>
                                    <p className="text-2xl font-bold text-teal-600">₹{selectedPricing.price * formData.selectedDates.length}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center text-red-800">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            <span className="text-sm">
                                Something went wrong. Please try again or contact support.
                            </span>
                        </div>
                    </div>
                )}

                {/* Payment Security Info */}
                {selectedPricing && selectedPricing.price > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center text-blue-800 text-sm">
                            <CreditCard className="h-4 w-4 mr-2" />
                            <span>
                                Secure payment processing by Razorpay. Your payment information is encrypted and safe.
                            </span>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex space-x-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={isSubmitting}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting || (selectedPricing && selectedPricing.price > 0 && !isRazorpayLoaded)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-lg hover:from-teal-600 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105">
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {selectedPricing?.price === 0 ? 'Booking...' : 'Processing Payment...'}
                            </span>
                        ) : (
                            <>
                                {selectedPricing?.price === 0 ? (
                                    `Book Now (Free)`
                                ) : selectedPricing ? (
                                    <span className="flex items-center justify-center">
                                        <CreditCard className="h-4 w-4 mr-1" />
                                        Pay ₹{
                                            (formData.pricingOption === 'session' || formData.pricingOption === 'demo') && formData.selectedDates.length > 0
                                                ? selectedPricing.price * formData.selectedDates.length
                                                : selectedPricing.price
                                        }
                                    </span>
                                ) : (
                                    'Select pricing to continue'
                                )}
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Terms */}
            <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 text-center">
                    By booking this class, you agree to our {' '}
                    <a href="#" className="text-teal-600 hover:underline">Terms of Service</a> and {' '}
                    <a href="#" className="text-teal-600 hover:underline">Privacy Policy</a>.
                    You will receive a confirmation email with class details.
                </p>
            </div>
        </div>

        {/* Booking Confirmation Modal */}
        {showConfirmationModal && pendingBookingData && (
            <BookingConfirmationModal
                isOpen={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirmBooking}
                bookingDetails={pendingBookingData.bookingDetails}
                isProcessing={isSubmitting}
            />
        )}
        </React.Fragment>
    );
}                           
